import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import ErrorModal from 'bigmailer/ErrorModal.component';
import EstimateModal from 'bigmailer/EstimateModal.component';
import cn from 'classnames';

import {
  beMarketingEmailDraftWrite,
  beMarketingEmailEstimateWrite,
  beMarketingEmailPollWrite,
  beMarketingEmailRead,
  beMarketingEmailResendInvite,
  beMarketingEmailWrite,
} from 'actions/email/beMarketingEmail.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import Space from 'components/generic/Space.component';
import UpgradePlanButton from 'components/hr/timeOffSplash/UpgradePlanButton.component';
import SM from 'components/sidebar/appointment/wizard/Wizard.module.scss';
import CONF from 'config/conf.singleton';
import DELAY from 'constants/delay.const';
import PLAN_NAME from 'constants/plan.const';
import USER_ROLE from 'constants/role';
import S from 'constants/string.const';
import { usePrepareReadWrite, usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectAccessPermissionType } from 'selectors/access.selectors';
import { selectCurrentSalonBilling, selectCurrentSalonName, selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { locationNew } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';
import C from 'utils/values/constant.util';

import CN from './BigMailerButton.module.scss';

const L = logger('BigMailerButton');

const UPLOAD_STATE = {
  fine: 'fine',
  loading: 'loading',
  complete: 'complete',
};

const PHASE1_PROGRESS_PERCENT = 25;
const PHASE2_PROGRESS_RANGE = 75;
const PHASE2_MULTIPLIER = 6;
const PHASE2_MIN_ESTIMATE_SEC = 30;
const PROGRESS_UPDATE_INTERVAL_MS = 1000;

const MODAL_TYPE = C({
  planBlock: 'planBlockModal',
  invitation: 'invitationModal',
  existingInvitation: 'existingInvitationModal',
  pendingInvite: 'pendingInviteModal',
  unverified: 'unverifiedViaAnotherSalonModal',
  error: 'errorModal',
  estimate: 'estimateModal',
  alreadyVerifiedNoBrand: 'alreadyVerifiedNoBrand',
});

const determineModalType = (accountData, hasBilling, canInvite) => {
  const { salonHasABrand, currentUserInvited, currentUserVerified } = accountData;

  if (!hasBilling || !canInvite) return MODAL_TYPE.planBlock;
  if (!salonHasABrand && currentUserInvited && !currentUserVerified) return MODAL_TYPE.unverified;
  if (salonHasABrand && !currentUserInvited && !currentUserVerified) return MODAL_TYPE.pendingInvite;
  if (!salonHasABrand && !currentUserInvited && !currentUserVerified) return MODAL_TYPE.invitation;
  if (salonHasABrand && currentUserInvited && !currentUserVerified) return MODAL_TYPE.existingInvitation;
  if (!salonHasABrand && currentUserInvited && currentUserVerified) return MODAL_TYPE.alreadyVerifiedNoBrand;
  if (salonHasABrand && currentUserInvited && currentUserVerified) return MODAL_TYPE.estimate;

  return null;
};

const BigMailerButton = ({ salonName, hasBilling, pricingPlan, clientsIds, permissionType }) => {
  const { t } = useTranslation();

  const {
    qj: resultAccountRead,
    qr: reFetchEmail,
    up: enableEmail,
  } = usePrepareReadWrite({
    reader: beMarketingEmailRead,
    writer: beMarketingEmailWrite,
  });

  const { salonHasABrand, currentUserInvited, currentUserVerified } = resultAccountRead ?? {};

  const { up: resendInvite } = usePrepareWriteOnly(beMarketingEmailResendInvite);
  const { up: estimateWrite } = usePrepareWriteOnly(beMarketingEmailEstimateWrite);
  const { isLoading: isEmailDesignCreating, up: draftWrite } = usePrepareWriteOnly(beMarketingEmailDraftWrite);
  const { up: pollEmailDraft } = usePrepareWriteOnly(beMarketingEmailPollWrite);

  const [estimateData, setEstimateData] = useState(null);
  const { val: inviteResent, set: setInviteResent } = useTip(false);
  const { val: isResendingInvite, set: setIsResendingInvite } = useTip(false);
  const { val: isCreatingEmail, set: setIsCreatingEmail } = useTip(false);

  const { val: isPlanBlockModal, yay: showPlanBlockModal, nay: hidePlanBlockModal } = useTip(false);
  const { val: isInvitationModalRendered, yay: showInvitationModal, nay: hideInvitationModal } = useTip(false);
  const { val: isEstimateRendered, yay: showEstimateModal, nay: hideEstimateModal } = useTip(false);

  const { val: isUnverifiedViaAnotherSalonModal, yay: showUnverifiedViaAnotherSalonModal, nay: hideUnverifiedViaAnotherSalonModal } = useTip(false);

  const { val: isDraftSuccessModal, yay: showDraftSuccessModal, nay: hideDraftSuccessModal } = useTip(false);
  const { val: isSuccessModal, yay: showSuccessModal, nay: hideSuccessModal } = useTip(false); // after POST when FTT
  const { val: isErrorModal, yay: showErrorModal, nay: hideErrorModal } = useTip(false);

  const [uploadState, setUploadState] = useState(UPLOAD_STATE.fine); // fine | loading
  const [campaignUrlForDraftModal, setCampaignUrlForDraftModal] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100

  const [errorMsg, setErrorMsg] = useState('');

  const isManager = permissionType === USER_ROLE.manager;
  // NOTE: beta_annual_salon is no needed for Email Blast flow, only  hasBilling + plan type
  const canInvite = hasBilling && (pricingPlan === PLAN_NAME.advanced || pricingPlan === PLAN_NAME.supercharged);

  const handleCreateEmailBlast = useCallback(async () => {
    if (isCreatingEmail) return;
    setIsCreatingEmail(true);

    try {
      const modalType = determineModalType(resultAccountRead, hasBilling, canInvite);

      if (!canInvite) {
        showPlanBlockModal();
        return;
      }

      // F T F: user invited via another salon but unverified
      if (modalType === MODAL_TYPE.unverified) {
        const response = await enableEmail();
        if (!response?.is?.ok || response?.originalJson?.errors?.details) {
          const errorDetails = response?.originalJson?.errors?.details;
          setErrorMsg(errorDetails);
          showErrorModal();
          return;
        }
        showUnverifiedViaAnotherSalonModal();
        return;
      }

      // T F F: salon added but not invited
      if (modalType === MODAL_TYPE.pendingInvite) {
        const response = await enableEmail(); // post
        if (!response?.is?.ok || response?.originalJson?.errors?.details) {
          const errorDetails = response?.originalJson?.errors?.details;
          setErrorMsg(errorDetails);
          showErrorModal();
          return;
        }

        await reFetchEmail?.refetch?.(); // refetch() data from 'qr' from useQuery

        showInvitationModal();
        return;
      }

      // F F F -> First invitation with POST -> Invitation modal
      if (modalType === MODAL_TYPE.invitation) {
        const response = await enableEmail(); // post
        if (!response?.is?.ok || response?.originalJson?.errors?.details) {
          const errorDetails = response?.originalJson?.errors?.details;
          setErrorMsg(errorDetails || t('clientsEmailPage.topBar.accountErrorMessage'));
          showErrorModal();
          return;
        }

        await reFetchEmail?.refetch?.(); // refetch() data from 'qr' from useQuery
        showInvitationModal();
        return;
      }

      // T T F Already invited -> Invitation modal
      if (modalType === MODAL_TYPE.existingInvitation) {
        showInvitationModal();
        return;
      }

      // F T T – verified user in a second salon without brand -> NO modal, just POST
      if (modalType === MODAL_TYPE.alreadyVerifiedNoBrand) {
        const response = await enableEmail();

        if (!response?.is?.ok || response?.originalJson?.errors?.details) {
          const errorDetails = response?.originalJson?.errors?.details;
          setErrorMsg(errorDetails);
          showErrorModal();
          return;
        }
        showSuccessModal();
        return;
      }

      // T T T -> Estimate modal
      if (modalType === MODAL_TYPE.estimate) {
        if (uploadState === UPLOAD_STATE.loading || uploadState === UPLOAD_STATE.complete) {
          return; // prevent duplicate list creation while previous upload is in progress
        }
        const response = await estimateWrite({ data: { recipients: clientsIds } });
        if (!response?.is?.ok || response?.originalJson?.errors?.details) {
          const errorDetails = response?.originalJson?.errors?.details;
          setErrorMsg(errorDetails || t('clientsEmailPage.topBar.estimateErrorMessage'));
          showErrorModal();
          return;
        }

        const estimate = {
          ...response.json, // expectedCount, expectedCost, cosPerEmail
          recipients: clientsIds,
        };
        setEstimateData(estimate);
        showEstimateModal();
      }
    } finally {
      setIsCreatingEmail(false);
    }
  }, [
    isCreatingEmail,
    setIsCreatingEmail,
    resultAccountRead,
    hasBilling,
    canInvite,
    showPlanBlockModal,
    enableEmail,
    showUnverifiedViaAnotherSalonModal,
    showErrorModal,
    reFetchEmail,
    showInvitationModal,
    t,
    showSuccessModal,
    estimateWrite,
    clientsIds,
    showEstimateModal,
    uploadState,
  ]);

  const handleResendInvite = useCallback(async () => {
    if (isResendingInvite || inviteResent) return;

    const shouldSend =
      (salonHasABrand && currentUserInvited && !currentUserVerified) ||
      (!salonHasABrand && currentUserInvited && !currentUserVerified) ||
      (salonHasABrand && !currentUserInvited && !currentUserVerified);

    if (!shouldSend) return;

    try {
      setIsResendingInvite(true);

      const response = await resendInvite();

      if (!response?.is?.ok || response?.originalJson?.errors?.details) {
        const errorDetails = response?.originalJson?.errors?.details || t('clientsEmailPage.topBar.resendErrorMessage');

        setErrorMsg(errorDetails);
        showErrorModal();
        return;
      }
      setInviteResent(true);
    } finally {
      setIsResendingInvite(false);
    }
  }, [isResendingInvite, inviteResent, salonHasABrand, currentUserInvited, currentUserVerified, setIsResendingInvite, resendInvite, setInviteResent, t, showErrorModal]);

  const pollUntilCampaignReady = useCallback(
    (importJobId) =>
      new Promise((resolve) => {
        if (!importJobId) {
          L.debug('pollUntilCampaignReady()', 'importJobId:', importJobId);
          return resolve();
        }
        // NOTE: Option / possible solution: using async/await with while and flag to avoiding resolve().
        const poll = async () => {
          try {
            const response = await pollEmailDraft({ data: { importJobId } });
            const campaignUrl = response?.json?.campaignUrl;

            // if error-> stop polling
            if (!response?.is?.ok) return resolve();

            // is ok
            if (campaignUrl) return resolve(campaignUrl);

            // not ready -> wait and try again after 3s
            delayed(DELAY.attention, poll);
          } catch (error) {
            L.debug('pollUntilCampaignReady() error:', error);
            resolve();
          }
        };

        poll(); // start again
      }),
    [pollEmailDraft],
  );

  const handleDesignEmail = useCallback(async () => {
    if (uploadState === UPLOAD_STATE.loading || uploadState === UPLOAD_STATE.complete) {
      L.warn('handleDesignEmail()', 'Upload already in progress, ignoring');
      return;
    }
    if (!estimateData?.recipients?.length) {
      L.warn('No recipients to create draft', estimateData);
      return;
    }

    setUploadState(UPLOAD_STATE.loading);
    setUploadProgress(0);

    const phase1Start = Date.now();
    const response = await draftWrite({ data: { recipients: estimateData.recipients } });

    // other error
    if (!response?.is?.ok || response?.originalJson?.errors?.details) {
      setUploadState(UPLOAD_STATE.fine);
      setUploadProgress(0);
      const errorDetails = response?.originalJson?.errors?.details;
      setErrorMsg(errorDetails || t('clientsEmailPage.topBar.draftErrorMessage'));
      showErrorModal();
      return;
    }

    const phase1DurationSec = (Date.now() - phase1Start) / 1000;
    setUploadProgress(PHASE1_PROGRESS_PERCENT); // Phase 1 (upload) complete

    const importId = response?.json?.importId;
    const phase2Start = Date.now();

    // Progress during polling: estimate phase 2 from phase 1 (backend often takes 5–10x upload time)
    const phase2EstimateSec = Math.max(phase1DurationSec * PHASE2_MULTIPLIER, PHASE2_MIN_ESTIMATE_SEC);
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - phase2Start) / 1000;
      const phase2Percent = Math.min(PHASE2_PROGRESS_RANGE - 1, (elapsed / phase2EstimateSec) * PHASE2_PROGRESS_RANGE);
      const percent = PHASE1_PROGRESS_PERCENT + phase2Percent;
      setUploadProgress(percent);
    }, PROGRESS_UPDATE_INTERVAL_MS);

    const campaignUrl = await pollUntilCampaignReady(importId);

    clearInterval(progressInterval);

    if (campaignUrl) {
      setUploadProgress(100);
      setUploadState(UPLOAD_STATE.complete);
      delayed(DELAY.uploadCompleteView, () => {
        hideEstimateModal();
        setCampaignUrlForDraftModal(campaignUrl);
        showDraftSuccessModal();
        delayed(DELAY.attention, () => locationNew({ url: campaignUrl }));
      });
    } else {
      setUploadState(UPLOAD_STATE.fine);
      setUploadProgress(0);
    }
  }, [estimateData, draftWrite, pollUntilCampaignReady, t, showErrorModal, hideEstimateModal, showDraftSuccessModal, uploadState]);

  const handleOpenDraftInSlickEmail = useCallback(() => {
    if (campaignUrlForDraftModal) {
      locationNew({ url: campaignUrlForDraftModal });
    }
  }, [campaignUrlForDraftModal]);

  const handleCloseDraftSuccessModal = useCallback(() => {
    setCampaignUrlForDraftModal(null);
    setUploadState(UPLOAD_STATE.fine);
    setUploadProgress(0);
    hideDraftSuccessModal();
  }, [hideDraftSuccessModal]);

  const handleCloseInvitationModal = () => {
    setInviteResent(false); // Need to reset state
    setIsResendingInvite(false);
    hideInvitationModal();
  };

  return (
    <div data-bem="BigMailerButton" className={CN.component}>
      {isManager && (
        <>
          {salonHasABrand && currentUserVerified && (
            <ExternalLink variant="buttonOutline" link={CONF.bigMailerDashboardUrl} tag="BigMailerButton__dashboard-link">
              <span className={CN.linkInnerContent}>
                <Icon variant="arrowOnSquare" width="md" selected />
                {t('clientsEmailPage.topBar.linkGoToEmail')}
              </span>
            </ExternalLink>
          )}

          <Button tag="BigMailerButton__create-email-button" variant="primary" onClick={handleCreateEmailBlast} disabled={isCreatingEmail}>
            <span className={CN.buttonContent}>
              {isCreatingEmail && <InlineSpinner variant="light" />}
              <span className={CN.buttonText}>
                <Icon variant="plus" width="md" />
                {t('clientsEmailPage.topBar.button')}
              </span>
            </span>
          </Button>
        </>
      )}
      {/* Invitation modal */}
      <SalonModal tag="BigMailerButton__invite-account" isOpen={isInvitationModalRendered} variant="noFooter">
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={handleCloseInvitationModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="info" width="lg">
            <Icon variant="envelope" width="xl" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={CN.modalTitle}>
              {inviteResent ? t('clientsEmailPage.invitationModal.modalTitleInviteSent') : t('clientsEmailPage.invitationModal.modalTitleInvite')}
              {!inviteResent && S.party}
            </div>
            {inviteResent ? (
              <div data-bem="BigMailerButton__resend-invite-sent" className={SM.modalText}>
                {t('clientsEmailPage.invitationModal.modalTextInviteSent1')}
                <br />
                {t('clientsEmailPage.invitationModal.modalTextInviteSent2')}
              </div>
            ) : (
              <div data-bem="BigMailerButton__resend-invite">
                <div className={SM.modalText}>
                  {t('clientsEmailPage.invitationModal.emailCreatedTitleStart')}
                  <Space bold>{salonName}</Space> {t('clientsEmailPage.invitationModal.emailCreatedTitleEnd')}
                </div>
                <br />
                <div className={CN.modalNextStep}>
                  <div className={CN.nextStepTitle}>{t('clientsEmailPage.invitationModal.emailCreatedNextStepTitle')}</div>
                  <div className={CN.nextStepSubTitle}>{t('clientsEmailPage.invitationModal.nextStepSubTitle')}</div>
                  <div className={CN.step}>
                    {S.dot} {t('clientsEmailPage.invitationModal.stepVerifyStart')}
                    <Space bold>{t('clientsEmailPage.invitationModal.stepVerifyCta')}</Space>
                    {t('clientsEmailPage.invitationModal.stepVerifyEnd')}
                  </div>
                  <div className={CN.step}>
                    {S.dot} {t('clientsEmailPage.invitationModal.stepCreatePassword')}
                  </div>
                  <div className={CN.step}>
                    {S.dot} {t('clientsEmailPage.invitationModal.stepRefresh')}
                  </div>
                </div>
              </div>
            )}

            {!inviteResent && (
              <div className={CN.modalResendContainer}>
                {t('clientsEmailPage.invitationModal.resendText1')} <br />
                {t('clientsEmailPage.invitationModal.resendText2')}
                <div
                  tag="BigMailerButton__resend-invite-button"
                  onClick={handleResendInvite}
                  role="button"
                  tabIndex={0}
                  className={cn({
                    [CN.resendButton]: true,
                    [CN.disabled]: isResendingInvite,
                  })}
                >
                  {isResendingInvite ? t('clientsEmailPage.invitationModal.sendingInviteButton') : t('clientsEmailPage.invitationModal.resendButton')}
                </div>
              </div>
            )}
          </div>
        </SalonModal.Body>
      </SalonModal>

      {/* Plan block modal */}
      <SalonModal tag="BigMailerButton__plan-block" isOpen={isPlanBlockModal} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={hidePlanBlockModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="info" width="lg">
            <Icon variant="envelope" width="l1" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('clientsEmailPage.topBar.upgradePlanTitle')}</div>
            <div className={cn(SM.modalText, SM.widthMd)}>{t('clientsEmailPage.topBar.upgradePlanDescription')}</div>
            <br />
            <div className={cn(SM.modalText, SM.widthMd)}>{t('clientsEmailPage.topBar.unlockAbilityMessage')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <UpgradePlanButton text={t('clientsEmailPage.topBar.upgradePlanButton')} />
        </SalonModal.Footer>
      </SalonModal>

      {/* Estimate modal  */}
      <EstimateModal
        tag="BigMailerButton__modal-estimate"
        isOpen={isEstimateRendered}
        onClose={hideEstimateModal}
        onDesign={handleDesignEmail}
        estimate={estimateData}
        hasNoClients={isEmptyArray(estimateData?.recipients)}
        disabled={isEmptyArray(estimateData?.recipients) || isEmailDesignCreating}
        isDesignCreating={isEmailDesignCreating}
        uploadState={uploadState}
        uploadProgress={uploadProgress}
        onTimeoutClose={hideEstimateModal}
      />

      {/* Unverified via another salon modal  */}
      <SalonModal tag="BigMailerButton__unverified-via-another-salon" isOpen={isUnverifiedViaAnotherSalonModal} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={hideUnverifiedViaAnotherSalonModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="info" width="lg">
            <Icon variant="envelope" width="l1" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('clientsEmailPage.topBar.verifyEmailTitle', { prefix: S.envelope })}</div>
            <div className={SM.modalText}>{t('clientsEmailPage.topBar.verifyEmailDescription', { salonName })}</div>
            <br />
            <div className={SM.modalText}>{t('clientsEmailPage.topBar.resendEmailMessage')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button tag="BigMailerButton__resend-verify-button" onClick={handleResendInvite} variant="primary" noMargin xSpace disabled={isResendingInvite || inviteResent}>
            {isResendingInvite ? t('clientsEmailPage.invitationModal.sendingInviteButton') : t('clientsEmailPage.invitationModal.resendButton')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      {/* Error modal */}
      <ErrorModal isOpen={isErrorModal} onClose={hideErrorModal} message={errorMsg} />

      {/* Draft creation success modal */}
      <SalonModal tag="BigMailerButton__draft-success-modal" isOpen={isDraftSuccessModal} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={handleCloseDraftSuccessModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon selected width="l1" variant="confirm" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('clientsEmailPage.topBar.draftCreatedTitle')}</div>
            <div className={SM.modalText}>{t('clientsEmailPage.topBar.draftCreatedDescription')}</div>
            <div className={SM.modalText}>{t('clientsEmailPage.topBar.draftCreatedCtaFallback')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button tag="BigMailerButton__open-draft-cta" variant="primary" noMargin xSpace onClick={handleOpenDraftInSlickEmail} disabled={!campaignUrlForDraftModal}>
            {t('clientsEmailPage.topBar.draftCreatedCta')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      {/* SuccessModal Setup complete! */}
      <SalonModal tag="BigMailerButton__success-modal" isOpen={isSuccessModal} variant="noFooter">
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={hideSuccessModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon selected width="l1" variant="confirm" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('clientsEmailPage.topBar.emailReadyTitle', { salonName })}</div>
            <div className={SM.modalText}>
              {t('clientsEmailPage.topBar.emailReadyDescription')} {S.party}
            </div>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const BigMailerButtonHoc = connect(
  (state) => ({
    salonName: selectCurrentSalonName(state),
    hasBilling: selectCurrentSalonBilling(state),
    pricingPlan: selectCurrentSalonPricingPlan(state),
    permissionType: selectAccessPermissionType(state),
  }),
  mapActionsToDispatchToProps({}),
)(BigMailerButton);

export default BigMailerButtonHoc;
